import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmailRichEditor from "@/components/shared/EmailRichEditor";
import { ImportLinkBox, GalleryUploader, AnnouncementBody } from "@/components/shared/AnnouncementBits";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from "@/lib/api";
import { ReadReceipts, utcToEt, fmtEt } from "@/components/shared/AnnouncementReads";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Megaphone, Plus, Trash2, Loader2, Bell, AlertTriangle, Info, CheckCircle, Pencil, Clock } from "lucide-react";

const priorityConfig = {
  urgent: { icon: AlertTriangle, color: "bg-red-100 text-red-700" },
  high: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700" },
  normal: { icon: Info, color: "bg-blue-100 text-blue-700" },
  low: { icon: CheckCircle, color: "bg-green-100 text-green-700" },
};

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const BLANK = { title: "", content: "", priority: "normal", target_audience: "all", active: true, images: [], source_url: null, email_dealers: null, publish_at: "" };
  const [newAnnouncement, setNewAnnouncement] = useState(BLANK);
  const [reads, setReads] = useState({ total: 0, counts: {} });
  const [editingScheduled, setEditingScheduled] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const openNew = () => { setEditingId(null); setEditingScheduled(false); setNewAnnouncement(BLANK); setEditorKey((k) => k + 1); setIsDialogOpen(true); };
  const openEdit = (a) => {
    setEditingId(a.id);
    const sched = a.published === false;
    setEditingScheduled(sched);
    setNewAnnouncement({ title: a.title, content: a.content || "", priority: a.priority || "normal", target_audience: a.target_audience || "all",
      active: a.active !== false, images: a.images || [], source_url: a.source_url || null, email_dealers: sched ? !!a.email_on_publish : false,
      publish_at: sched ? utcToEt(a.publish_at) : "" });
    setEditorKey((k) => k + 1);
    setIsDialogOpen(true);
  };
  const saveEdit = async (toDealers) => {
    if (toDealers && newAnnouncement.email_dealers && !editingScheduled && !window.confirm("Email the updated announcement to every approved dealer?")) return;
    setSubmitting(true);
    try {
      await updateAnnouncement(editingId, { ...newAnnouncement, publish_at: newAnnouncement.publish_at || null, email_dealers: toDealers && newAnnouncement.email_dealers });
      toast.success(editingScheduled ? "Scheduled announcement updated" : toDealers && newAnnouncement.email_dealers ? "Announcement updated — emailing dealers now" : "Announcement updated");
      setIsDialogOpen(false);
      setEditingId(null);
      setNewAnnouncement(BLANK);
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to update announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const data = await getAnnouncements(false);
      setAnnouncements(data);
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/read-stats`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
        .then((r) => setReads(r.data)).catch(() => {});
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncement.content.replace(/<[^>]+>/g, "").trim() && !/<img/i.test(newAnnouncement.content) && !newAnnouncement.images.length) {
      toast.error("Add some content or images");
      return;
    }
    const toDealers = newAnnouncement.target_audience !== "admins";
    if (editingId) { await saveEdit(toDealers); return; }
    if (toDealers && newAnnouncement.email_dealers === null) {
      toast.error("Choose 'Email dealers' or 'Portal only'");
      return;
    }
    const later = !!newAnnouncement.publish_at;
    if (toDealers && newAnnouncement.email_dealers && !later && !window.confirm("This will email every approved dealer. Post and send now?")) return;
    setSubmitting(true);

    try {
      await createAnnouncement({ ...newAnnouncement, publish_at: newAnnouncement.publish_at || null, email_dealers: newAnnouncement.target_audience !== "admins" && newAnnouncement.email_dealers });
      toast.success(later ? `Scheduled for ${newAnnouncement.publish_at.replace("T", " ")} ET` : newAnnouncement.email_dealers && newAnnouncement.target_audience !== "admins" ? "Announcement posted — emailing dealers now" : "Announcement posted to the portal");
      setIsDialogOpen(false);
      setNewAnnouncement(BLANK);
      setEditorKey((k) => k + 1);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;

    try {
      await deleteAnnouncement(announcementId);
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  return (
    <AdminLayout title="Announcements" breadcrumb="Announcements">
      <div className="space-y-6" data-testid="admin-announcements-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-sm text-slate-500 font-body">
            <Bell className="w-4 h-4" />
            {announcements.length} announcements
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={(e) => { e.preventDefault(); openNew(); }} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-announcement-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-navy">{editingId ? "Edit Announcement" : "Create Announcement"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAnnouncement} className="space-y-4" data-testid="create-announcement-form">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    required
                    placeholder="Announcement title"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div>
                  <Label>Import from a link (optional)</Label>
                  <ImportLinkBox onImported={(d) => { setNewAnnouncement((p) => ({ ...p, title: p.title || d.title, content: d.content, source_url: d.source_url })); setEditorKey((k) => k + 1); }} />
                </div>
                <div>
                  <Label>Content</Label>
                  <div data-testid="announcement-content-input">
                    <EmailRichEditor key={editorKey} value={newAnnouncement.content} onChange={(html) => setNewAnnouncement((p) => ({ ...p, content: html }))} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Paste straight from an email, or add images anywhere with the image button.</p>
                </div>
                <div>
                  <Label>Image gallery (optional)</Label>
                  <GalleryUploader images={newAnnouncement.images} onChange={(images) => setNewAnnouncement((p) => ({ ...p, images }))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={newAnnouncement.priority}
                      onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, priority: value })}
                    >
                      <SelectTrigger data-testid="announcement-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="target_audience">Target Audience</Label>
                    <Select
                      value={newAnnouncement.target_audience}
                      onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, target_audience: value })}
                    >
                      <SelectTrigger data-testid="announcement-target">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Everyone</SelectItem>
                        <SelectItem value="dealers">Dealers Only</SelectItem>
                        <SelectItem value="admins">Admins Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="active"
                    checked={newAnnouncement.active}
                    onCheckedChange={(checked) => setNewAnnouncement({ ...newAnnouncement, active: checked })}
                    data-testid="announcement-active-switch"
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
                {(!editingId || editingScheduled) && (
                  <div>
                    <Label htmlFor="publish_at">Schedule for later (Eastern Time)</Label>
                    <Input id="publish_at" type="datetime-local" value={newAnnouncement.publish_at} required={editingScheduled}
                      onChange={(e) => setNewAnnouncement((p) => ({ ...p, publish_at: e.target.value }))} className="mt-1 max-w-xs" data-testid="announcement-publish-at" />
                    <p className="text-xs text-slate-400 mt-1">{editingScheduled ? "Change when it posts." : "Leave blank to post right now."} Dealers won't see it until then.</p>
                  </div>
                )}
                {editingId && !editingScheduled && newAnnouncement.target_audience !== "admins" && (
                  <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                    <Switch id="resend" checked={!!newAnnouncement.email_dealers} onCheckedChange={(v) => setNewAnnouncement((p) => ({ ...p, email_dealers: v }))} data-testid="announcement-resend-switch" />
                    <Label htmlFor="resend" className="cursor-pointer">Email the updated version to dealers <span className="block text-xs font-normal text-slate-500">Off = the change only shows in the portal</span></Label>
                  </div>
                )}
                {(!editingId || editingScheduled) && newAnnouncement.target_audience !== "admins" && (
                  <div>
                    <Label>How should dealers get it?</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1" data-testid="announcement-delivery">
                      {[[true, "Email dealers", "Portal + an email to every dealer"], [false, "Portal only", "Shows in the dealer portal only"]].map(([v, l, d]) => (
                        <button key={l} type="button" onClick={() => setNewAnnouncement((p) => ({ ...p, email_dealers: v }))}
                          data-testid={v ? "announcement-email-dealers-btn" : "announcement-portal-only-btn"}
                          className={`rounded-lg border p-3 text-left transition-colors ${newAnnouncement.email_dealers === v ? "border-teal bg-teal/5 ring-1 ring-teal" : "border-slate-200 hover:bg-slate-50"}`}>
                          <p className="text-sm font-semibold text-navy">{l}</p><p className="text-xs text-slate-500">{d}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal hover:bg-teal-dark text-white"
                  data-testid="submit-announcement-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingId ? "Saving..." : "Creating..."}
                    </>
                  ) : (
                    editingId ? "Save Changes" : "Create Announcement"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Announcements List */}
        {loading ? (
          <p className="text-slate-500 text-center py-8 font-body">Loading announcements...</p>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-body">No announcements yet</p>
            <p className="text-slate-400 text-sm font-body">Create your first announcement to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement, index) => {
              const config = priorityConfig[announcement.priority] || priorityConfig.normal;
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="border-slate-200" data-testid={`announcement-card-${announcement.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="font-heading font-semibold text-navy">
                                {announcement.title}
                              </h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.color}`}>
                                {announcement.priority}
                              </span>
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                                {announcement.target_audience}
                              </span>
                              {!announcement.active && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                  Inactive
                                </span>
                              )}
                              {announcement.published === false && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800" data-testid={`announcement-scheduled-${announcement.id}`}>
                                  <Clock className="w-3 h-3" />Scheduled · {fmtEt(announcement.publish_at)}{announcement.email_on_publish ? " · will email" : ""}
                                </span>
                              )}
                            </div>
                            <div className="mb-2 max-h-72 overflow-y-auto"><AnnouncementBody a={announcement} /></div>
                            <p className="text-slate-400 text-xs font-body">
                              Created {new Date(announcement.created_at).toLocaleDateString()}
                              {announcement.updated_at && ` · Edited ${new Date(announcement.updated_at).toLocaleDateString()}`}
                              {announcement.emailed_count != null && ` · Emailed to ${announcement.emailed_count} dealer(s)`}
                            </p>
                            {announcement.published !== false && announcement.target_audience !== "admins" && (
                              <div className="mt-1"><ReadReceipts id={announcement.id} count={reads.counts[announcement.id] || 0} total={reads.total} /></div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(announcement)} title="Edit" aria-label="Edit announcement" data-testid={`edit-announcement-${announcement.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-announcement-${announcement.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
